import { SnippetChunk } from "@/components/item-list/types";
import { ApiSchema } from "@/modules/api";

type SearchEngineResultItemNoteValue = ApiSchema["SearchEngineResultSectionItemNoteValue"];
export type SearchResultFeaturedSnippetRows = SnippetChunk[][];

/**
 * Resolves the snippet value passed to `ItemListRowFeaturedContent` for search note rows.
 *
 * Behavior priorities:
 * 1. PDF/image attachment matches determine the snippet whenever present:
 *    - one PDF match with page -> "Mentioned in page X"
 *    - multiple PDF matches -> "N matches"
 *    - image-only matches -> "Matching content in image(s)"
 *    - mixed PDF + image -> "Matching content in attachments"
 * 2. Without PDF/image attachment matches:
 *    - return backend snippets when present,
 *    - otherwise fallback to `secondaryLabel` (title/semantic cases).
 *
 * Returns one row in most cases and two rows when:
 * - note-body highlighted snippet exists (`isHighlight: true`) AND
 * - attachment summary text also exists.
 */
export const resolveSearchResultFeaturedSnippet = ({
  secondaryLabel,
  itemValue,
}: {
  secondaryLabel: string;
  itemValue: SearchEngineResultItemNoteValue;
}): SearchResultFeaturedSnippetRows => {
  const highlightedSnippet = mapHighlightedSnippetToItemListSnippet({
    itemValue,
  });
  const hasHighlightedNoteBodySnippet = highlightedSnippet.some(chunk => chunk.isHighlight);
  const attachmentSummary = resolveAttachmentSummarySnippetText({
    itemValue,
  });

  // Attachments take precedence over snippets returned by the search engine.
  if (attachmentSummary) {
    // If there is a highlighted note-body snippet, we include it above the attachment summary.
    // This is for the scenario where there's both a keyword match (whence the highlighted snippet)
    // and an attachment match.
    if (hasHighlightedNoteBodySnippet) {
      return [highlightedSnippet, [{ text: attachmentSummary }]];
    }

    return [[{ text: attachmentSummary }]];
  }

  // Returned by the search engine.
  if (highlightedSnippet.length > 0) {
    return [highlightedSnippet];
  }

  // There's no keyword match, render the secondary label.
  return [[{ text: secondaryLabel }]];
};

const mapHighlightedSnippetToItemListSnippet = ({
  itemValue,
}: {
  itemValue: SearchEngineResultItemNoteValue;
}): SnippetChunk[] => {
  if (!itemValue.highlighted_snippets?.length) {
    return [];
  }

  return itemValue.highlighted_snippets.map(
    ({ text, is_highlight, is_truncated_start, is_truncated_end }) => ({
      text,
      isHighlight: is_highlight,
      isTruncatedStart: is_truncated_start,
      isTruncatedEnd: is_truncated_end,
    })
  );
};

const resolveAttachmentSummarySnippetText = ({
  itemValue,
}: {
  itemValue: SearchEngineResultItemNoteValue;
}): string | undefined => {
  const attachmentMatches = itemValue.attachment_match_infos ?? [];
  if (!attachmentMatches.length) {
    return undefined;
  }

  const pdfMatches = attachmentMatches.filter(match => match.attachment_type === "pdf");
  const imageMatches = attachmentMatches.filter(match => match.attachment_type === "image");

  if (pdfMatches.length > 0 && imageMatches.length > 0) {
    return "Matching content in attachments";
  }

  if (imageMatches.length > 0) {
    const uniqueImageIds = new Set(imageMatches.map(match => match.attachment_id));
    return uniqueImageIds.size > 1 ? "Matching content in images" : "Matching content in image";
  }

  if (pdfMatches.length === 1) {
    const firstPageNumber = pdfMatches[0]?.page_number;
    if (typeof firstPageNumber === "number" && firstPageNumber > 0) {
      return `Mentioned in page ${firstPageNumber}`;
    }

    return "1 match";
  }

  if (pdfMatches.length > 1) {
    return `${pdfMatches.length} matches`;
  }

  return undefined;
};
